import { AggregateDatadogLogger } from '@tv2-player/datadog-logger';

interface ParsedResult {
  companyContexts: { namespace: string; value: string }[];
  users: { userIDs: { namespace: string; value: string; type: string }[] }[];
}

export function handleGetAllIdentifiersResult(jsonResult: string): string {
  const logger = new AggregateDatadogLogger();

  const defaultUserId = '0'; // It's not completely clear if this is a good default, or if we will ever get to use it.

  // Result example: {"companyContexts":[{"namespace":"imsOrgID","value":"86CA2B31536B6DE60A490D4C@AdobeOrg"}],"users":[{"userIDs":[{"namespace":"4","value":"56281828611378587592415057067950649407","type":"namespaceId"}]}]}

  try {
    if (jsonResult === null || jsonResult.length === 0) {
      return defaultUserId;
    }

    const parsed = JSON.parse(jsonResult) as ParsedResult | null;

    if (parsed === null || typeof parsed !== 'object') {
      logJsonAssertionFailed(
        'Got empty result from ADBMobile.config.getAllIdentifiersAsync - maybe this is an error?'
      );
      return defaultUserId;
    }

    if (parsed.users.length > 1) {
      logJsonAssertionFailed(
        'We got multiple users from ADBMobile.config.getAllIdentifiersAsync. We will use the first one. We need to find out if that is the correct thing to do'
      );
    }

    if (parsed.users[0].userIDs.length > 1) {
      // We will use the one with the type 'namespaceId' if there are multiple users.
      const namespaceIdUser = parsed.users[0].userIDs.find(user => user.type === 'namespaceId');
      if (namespaceIdUser) {
        return namespaceIdUser.value;
      }

      logJsonAssertionFailed(
        'We got multiple user ids from ADBMobile.config.getAllIdentifiersAsync - but we did not fond any with the type of namespaceId. We will use the first one. We need to find out if that is the correct thing to do'
      );
    }

    const userId = parsed.users[0].userIDs[0].value;

    if (!userId) {
      logJsonAssertionFailed(
        'We got a user id from ADBMobile.config.getAllIdentifiersAsync, but it was empty. We need to find out if that is the correct thing to send the default value'
      );
      return defaultUserId;
    }

    return userId;
  } catch (e) {
    logger.error(
      'Error getting Adobe Visitor Marketing Cloud User ID from ADBMobile',
      { json: jsonResult },
      e as any
    );
    return defaultUserId;
  }

  function logJsonAssertionFailed(message: string) {
    logger.error(`Assertion failed: ${message}`, { json: jsonResult });
  }
}

/**
 * Get Adobe Visitor Marketing Cloud User ID from ADBMobile
 *
 * It does that by taking the first user ID from the result of ADBMobile.config.getAllIdentifiersAsync
 * It is unclear if there can be any more than one user id in the result - but until we see a case with more than one, we will just take the first.
 *
 * @see https://adobe-marketing-cloud.github.io/media-sdks/reference/chromecast/ADBMobile.config.html#.getAllIdentifiersAsync
 */
export function getAdobeVisitorMarketingCloudUserIdFromADBMobile() {
  return new Promise<string>(resolve => {
    (window as any).ADBMobile.config.getAllIdentifiersAsync((jsonString: string) =>
      resolve(handleGetAllIdentifiersResult(jsonString))
    );
  });
}
